import { useMemo, useState } from "react";

/**
 * Card header for an event.
 *
 * If the event has a photo, show it — and if that URL fails to load
 * (org removed it, hotlink blocked), fall through to generated art.
 *
 * Generated art is unique per event: a palette from the category and a
 * kolam-style pattern seeded by the title, so no two cards look alike
 * even when twenty temples all post "Diwali".
 */

const PALETTES = {
  Festival:        ["#F5B940", "#E8562A", "#7A1F3D"],
  Religious:       ["#E9A23B", "#B5432F", "#4A1B0C"],
  Concert:         ["#7F77DD", "#3C3489", "#1B1740"],
  Cultural:        ["#D4537E", "#993556", "#3D1020"],
  "Kids & Family": ["#5DCAA5", "#1D9E75", "#085041"],
  Sports:          ["#378ADD", "#185FA5", "#042C53"],
  Food:            ["#EF9F27", "#BA7517", "#412402"],
  "Tech Networking": ["#5DCAA5", "#0F6E56", "#04342C"],
  Community:       ["#F0997B", "#D85A30", "#4A1B0C"],
  // legacy categories
  Diwali: ["#F5B940", "#E8562A", "#7A1F3D"], Holi: ["#D4537E", "#7F77DD", "#1D9E75"],
  Cricket: ["#378ADD", "#185FA5", "#042C53"], "Biryani Cook-offs": ["#EF9F27", "#BA7517", "#412402"],
  "Kids Playdates": ["#5DCAA5", "#1D9E75", "#085041"],
};
const DEFAULT = ["#F0997B", "#D85A30", "#4A1B0C"];

function hash(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return Math.abs(h);
}

// Six motif generators; each returns SVG markup for a 40x40 tile.
const MOTIFS = [
  (c) => `<circle cx="20" cy="10" r="6" fill="none" stroke="${c}" stroke-width="1.4"/><circle cx="20" cy="30" r="6" fill="none" stroke="${c}" stroke-width="1.4"/><circle cx="10" cy="20" r="6" fill="none" stroke="${c}" stroke-width="1.4"/><circle cx="30" cy="20" r="6" fill="none" stroke="${c}" stroke-width="1.4"/>`,
  (c) => `<path d="M20 6 L34 20 L20 34 L6 20 Z" fill="none" stroke="${c}" stroke-width="1.4"/><path d="M20 13 L27 20 L20 27 L13 20 Z" fill="none" stroke="${c}" stroke-width="1.2"/>`,
  (c) => `<path d="M20 5 C28 12 28 28 20 35 C12 28 12 12 20 5 Z" fill="none" stroke="${c}" stroke-width="1.4"/><path d="M5 20 C12 12 28 12 35 20 C28 28 12 28 5 20 Z" fill="none" stroke="${c}" stroke-width="1.4"/>`,
  (c) => `<path d="M20 7 L31 13 L31 27 L20 33 L9 27 L9 13 Z" fill="none" stroke="${c}" stroke-width="1.4"/><circle cx="20" cy="20" r="4" fill="${c}"/>`,
  (c) => `<path d="M20 4 L20 36 M4 20 L36 20 M9 9 L31 31 M31 9 L9 31" stroke="${c}" stroke-width="1.2"/><circle cx="20" cy="20" r="5" fill="none" stroke="${c}" stroke-width="1.4"/>`,
  (c) => `<path d="M20 6 Q26 13 20 20 Q14 13 20 6 Z M34 20 Q27 26 20 20 Q27 14 34 20 Z M20 34 Q14 27 20 20 Q26 27 20 34 Z M6 20 Q13 14 20 20 Q13 26 6 20 Z" fill="none" stroke="${c}" stroke-width="1.3"/>`,
];

export function eventArtDataUri(title, category) {
  const [light, mid, dark] = PALETTES[category] || DEFAULT;
  const h = hash((title || "event").toLowerCase());
  const motif = MOTIFS[h % MOTIFS.length];
  const angle = (h >> 3) % 360;
  const scale = 0.85 + ((h >> 6) % 5) * 0.12;   // 0.85 – 1.33 tile size
  const shift = (h >> 9) % 40;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="240" viewBox="0 0 600 240">
    <defs>
      <linearGradient id="g" gradientTransform="rotate(${angle})"><stop offset="0" stop-color="${light}"/><stop offset="0.55" stop-color="${mid}"/><stop offset="1" stop-color="${dark}"/></linearGradient>
      <pattern id="p" width="${40 * scale}" height="${40 * scale}" patternUnits="userSpaceOnUse" patternTransform="translate(${shift} ${shift})">
        <g transform="scale(${scale})">${motif("rgba(255,255,255,0.28)")}</g>
      </pattern>
    </defs>
    <rect width="600" height="240" fill="url(#g)"/>
    <rect width="600" height="240" fill="url(#p)"/>
    <rect width="600" height="240" fill="url(#g)" opacity="0.15"/>
  </svg>`;
  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
}

export function EventArt({ photo, title, category, alt = "", className = "" }) {
  const [broken, setBroken] = useState(false);
  const art = useMemo(() => eventArtDataUri(title, category), [title, category]);
  const src = photo && !broken ? photo : art;
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setBroken(true)}
      className={className}
    />
  );
}
